import { useMemo, useState } from "react";
import { Table2, Map } from "lucide-react";
import { fmt } from "../../lib/api";
import { RepHeatMap } from "./RepHeatMap";
import { testProviderLabel } from "../../lib/servers";
import { useSort, SortTh } from "../../lib/useSort";

const UT = [["created_at", "Time", (t) => t.created_at], ["team", "Province", (t) => t.team], ["city", "Location", (t) => t.location?.city], ["isp", "ISP", (t) => t.isp],
  ["provider", "Test provider", testProviderLabel], ["dl", "↓ Mbps", (t) => t.download_mbps], ["ul", "↑ Mbps", (t) => t.upload_mbps], ["ping", "Ping", (t) => t.ping_ms],
  ["jitter", "Jitter", (t) => t.jitter_ms], ["status", "Status", (t) => t.issues.length]];
const RS = [["rep", "Phone", (r) => r.rep], ["teams", "Provinces", (r) => [...r.teams].join(", ")], ["n", "Tests", (r) => r.n], ["dl", "Avg ↓ Mbps", (r) => r.dl / r.n],
  ["ul", "Avg ↑ Mbps", (r) => r.ul / r.n], ["ping", "Avg ping", (r) => r.ping / r.n], ["issues", "Issue tests", (r) => r.issues], ["last", "Last test", (r) => r.last]];
const accOf = (cols) => Object.fromEntries(cols.map(([k, , f]) => [k, f]));
const UT_ACC = accOf(UT);
const RS_ACC = accOf(RS);
const SortHead = ({ cols, s }) => (
  <thead className="text-neutral-500 text-left"><tr>{cols.map(([k, l]) => <SortTh key={k} k={k} label={l} sort={s.sort} toggle={s.toggle} className="px-6 py-2" />)}</tr></thead>
);

const statRows = (tests) => {
  const m = {};
  tests.forEach((t) => {
    const r = (m[t.rep_name] ||= { rep: t.rep_name, teams: new Set(), n: 0, dl: 0, ul: 0, ping: 0, issues: 0, last: t.created_at });
    r.teams.add(t.team); r.n++; r.dl += t.download_mbps; r.ul += t.upload_mbps; r.ping += t.ping_ms;
    if (t.issues.length) r.issues++;
    if (t.created_at > r.last) r.last = t.created_at;
  });
  return Object.values(m).sort((a, b) => b.issues - a.issues);
};


const UserTests = ({ tests }) => {
  const s = useSort(tests, UT_ACC);
  return (
  <table className="w-full text-xs border-t border-white/10" data-testid="rep-stats-user-tests">
    <SortHead cols={UT} s={s} />
    <tbody>
      {s.sorted.map((t) => (
        <tr key={t.id} className="border-t border-white/5">
          <td className="px-6 py-2 text-neutral-500">{new Date(t.created_at).toLocaleString()}</td>
          <td className="px-6 py-2">{t.team}</td>
          <td className="px-6 py-2">{t.location?.city || "—"}</td>
          <td className="px-6 py-2">{t.isp || "—"}</td>
          <td className="px-6 py-2 whitespace-nowrap">{testProviderLabel(t)}</td>
          <td className="px-6 py-2">{fmt(t.download_mbps)}</td>
          <td className="px-6 py-2">{fmt(t.upload_mbps)}</td>
          <td className="px-6 py-2">{fmt(t.ping_ms, 0)}</td>
          <td className="px-6 py-2">{fmt(t.jitter_ms, 0)}</td>
          <td className="px-6 py-2">{t.issues.length ? <span className="text-[#FF3B30]">{t.issues.length} issue(s)</span> : <span className="text-[#32D74B]">OK</span>}</td>
        </tr>
      ))}
    </tbody>
  </table>
  );
};

export const RepStats = ({ tests }) => {
  const [phone, setPhone] = useState("");
  const [mode, setMode] = useState("table");
  const all = useMemo(() => statRows(tests), [tests]);
  const scoped = useMemo(() => (phone ? tests.filter((t) => t.rep_name === phone) : tests), [tests, phone]);
  const rows = useMemo(() => (phone ? all.filter((r) => r.rep === phone) : all), [all, phone]);
  const rs = useSort(rows, RS_ACC);

  return (
    <section className="border-b border-white/10" data-testid="rep-stats">
      <div className="px-6 py-3 border-b border-white/10 flex flex-wrap items-center justify-between gap-3">
        <span className="label">Rep stats {phone ? `· ${phone} (${scoped.length} tests)` : "(by phone)"}</span>
        <div className="flex items-center gap-2">
          <select data-testid="rep-stats-phone-filter" value={phone} onChange={(e) => setPhone(e.target.value)} className="field py-2 text-xs">
            <option value="">All users ({all.length})</option>
            {all.map((r) => <option key={r.rep} value={r.rep}>{r.rep} · {r.n} tests</option>)}
          </select>
          <div className="flex border border-white/10" data-testid="rep-stats-view-toggle">
            {[["table", Table2, "Table"], ["map", Map, "Heat map"]].map(([k, Icon, l]) => (
              <button key={k} data-testid={`rep-stats-view-${k}`} onClick={() => setMode(k)}
                className={`px-3 py-2 text-xs flex items-center gap-1 transition-colors ${mode === k ? "bg-[#007AFF] text-white" : "text-neutral-400 hover:text-white"}`}>
                <Icon className="w-3.5 h-3.5" /> {l}
              </button>
            ))}
          </div>
        </div>
      </div>
      {mode === "map" ? <RepHeatMap tests={scoped} /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <SortHead cols={RS} s={rs} />
            <tbody>
              {rs.sorted.map((r) => (
                <tr key={r.rep} onClick={() => setPhone(r.rep)} className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer" data-testid={`rep-stats-row-${r.rep}`}>
                  <td className="px-6 py-2 text-white">{r.rep}</td>
                  <td className="px-6 py-2">{[...r.teams].join(", ")}</td>
                  <td className="px-6 py-2">{r.n}</td>
                  <td className="px-6 py-2">{fmt(r.dl / r.n)}</td>
                  <td className="px-6 py-2">{fmt(r.ul / r.n)}</td>
                  <td className="px-6 py-2">{fmt(r.ping / r.n, 0)} ms</td>
                  <td className={`px-6 py-2 ${r.issues ? "text-[#FF3B30]" : "text-[#32D74B]"}`}>{r.issues} / {r.n}</td>
                  <td className="px-6 py-2 text-neutral-500">{new Date(r.last).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <div className="px-6 py-4 text-xs text-neutral-600">No data yet.</div>}
          {phone && <UserTests tests={scoped} />}
        </div>
      )}
    </section>
  );
};
